"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "../../../hooks/useToast"; // You'll need to create this hook

const CaseStudyViewer = ({
  caseStudy,
  onClose,
  onDownloadPDF,
  onShare,
  className = "",
}) => {
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [selectedShareType, setSelectedShareType] = useState(null);
  const viewerRef = useRef(null);
  const galleryRef = useRef(null);

  // If you don't have a toast hook, create a simple one:
  // const { toast } = useToast();
  // For now, we'll use a simple state-based toast
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const formatRole = (role) =>
    role
      .replace(/([A-Z])/g, " $1") // add space before capitals
      .replace(/^./, (c) => c.toUpperCase()); // capitalize first letter
  const capitalizeWords = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());
  // Scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(viewerRef.current?.scrollTop > 50);
    };
    viewerRef.current?.addEventListener("scroll", handleScroll);
    return () => viewerRef.current?.removeEventListener("scroll", handleScroll);
  }, []);

  // Gallery navigation with keyboard shortcuts
  const handlePrevImage = useCallback(() => {
    if (caseStudy.gallery?.length) {
      setCurrentGalleryIndex((prev) =>
        prev === 0 ? caseStudy.gallery.length - 1 : prev - 1,
      );
    }
  }, [caseStudy.gallery]);

  const handleNextImage = useCallback(() => {
    if (caseStudy.gallery?.length) {
      setCurrentGalleryIndex((prev) =>
        prev === caseStudy.gallery.length - 1 ? 0 : prev + 1,
      );
    }
  }, [caseStudy.gallery]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Escape") {
        onClose();
        if (document.fullscreenElement) {
          document.exitFullscreen();
          setIsFullscreen(false);
        }
      }
      if (e.key === "ArrowLeft") handlePrevImage();
      if (e.key === "ArrowRight") handleNextImage();
      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        if (caseStudy.gallery?.[currentGalleryIndex]?.type === "video") {
          const video = document.querySelector("video");
          if (video) {
            video.paused ? video.play() : video.pause();
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [
    onClose,
    handlePrevImage,
    handleNextImage,
    currentGalleryIndex,
    caseStudy.gallery,
  ]);

  // Fullscreen toggle with proper error handling
  const toggleFullscreen = useCallback(async () => {
    if (!galleryRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await galleryRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
      setToast({
        show: true,
        message: "Fullscreen mode is not supported in this browser",
        type: "error",
      });
      setTimeout(
        () => setToast({ show: false, message: "", type: "success" }),
        3000,
      );
    }
  }, []);

  // Download PDF with progress simulation
  const handleDownloadPDF = useCallback(async () => {
    if (!caseStudy.id) return;

    setIsDownloading(true);

    try {
      const res = await fetch(`/api/case-studies/${caseStudy.id}/download`);

      if (!res.ok) throw new Error("Failed");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${caseStudy.title || "case-study"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);

      setToast({
        show: true,
        message: "PDF downloaded successfully!",
        type: "success",
      });
    } catch (err) {
      setToast({
        show: true,
        message: "Download failed.",
        type: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  }, [caseStudy]);

  // Comprehensive share functionality
  const handleShare = useCallback(
    async (type) => {
      if (!caseStudy.id) return;

      setIsSharing(true);
      setSelectedShareType(type);
      setCopiedLink(false);

      const shareUrl =
        typeof window !== "undefined"
          ? `${window.location.origin}/case-studies/${caseStudy.id}`
          : "";
      const shareTitle = `Case Study: ${caseStudy.title}`;
      const shareText = `Check out this case study: ${caseStudy.title}`;

      try {
        switch (type) {
          case "linkedin":
            window.open(
              `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
              "_blank",
              "noopener,noreferrer",
            );
            break;

          case "twitter":
            window.open(
              `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
              "_blank",
              "noopener,noreferrer",
            );
            break;

          case "facebook":
            window.open(
              `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
              "_blank",
              "noopener,noreferrer",
            );
            break;

          case "link":
            // Copy to clipboard
            await navigator.clipboard.writeText(shareUrl);
            setCopiedLink(true);
            setToast({
              show: true,
              message: "Link copied to clipboard!",
              type: "success",
            });
            break;

          case "email":
            window.open(
              `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`,
              "_blank",
            );
            break;

          default:
            // Use Web Share API if available
            if (navigator.share) {
              await navigator.share({
                title: shareTitle,
                text: shareText,
                url: shareUrl,
              });
            } else {
              await navigator.clipboard.writeText(shareUrl);
              setCopiedLink(true);
              setToast({
                show: true,
                message: "Link copied to clipboard!",
                type: "success",
              });
            }
        }

        // Call the original onShare prop if provided
        if (onShare) {
          await onShare(caseStudy.id, type);
        }
      } catch (error) {
        console.error("Share error:", error);
        if (error.name !== "AbortError") {
          setToast({
            show: true,
            message: "Failed to share. Please try again.",
            type: "error",
          });
        }
      } finally {
        setIsSharing(false);
        setTimeout(() => {
          setSelectedShareType(null);
          setToast({ show: false, message: "", type: "success" });
        }, 2000);
      }
    },
    [caseStudy.id, caseStudy.title, onShare],
  );

  // Drag & drop for gallery
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragDistance, setDragDistance] = useState(0);

  const handleDragStart = (e) => {
    setIsDragging(true);
    setDragStartX(e.type.includes("mouse") ? e.clientX : e.touches[0].clientX);
    setDragDistance(0);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const currentX = e.type.includes("mouse")
      ? e.clientX
      : e.touches[0].clientX;
    const distance = dragStartX - currentX;
    setDragDistance(distance);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    const threshold = 50;
    if (dragDistance > threshold) {
      handleNextImage();
    } else if (dragDistance < -threshold) {
      handlePrevImage();
    }

    setIsDragging(false);
    setDragDistance(0);
  };

  // Touch event handlers for mobile gallery
  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery || !caseStudy.gallery?.length) return;

    const handleTouchStart = (e) => handleDragStart(e);
    const handleTouchMove = (e) => handleDragMove(e);
    const handleTouchEnd = () => handleDragEnd();

    gallery.addEventListener("touchstart", handleTouchStart, { passive: true });
    gallery.addEventListener("touchmove", handleTouchMove, { passive: true });
    gallery.addEventListener("touchend", handleTouchEnd);

    return () => {
      gallery.removeEventListener("touchstart", handleTouchStart);
      gallery.removeEventListener("touchmove", handleTouchMove);
      gallery.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleDragStart, handleDragMove, handleDragEnd, caseStudy.gallery]);

  // Body scroll lock with cleanup
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    };
  }, []);

  // Auto-advance gallery (optional)
  useEffect(() => {
    if (caseStudy.gallery?.length <= 1) return;

    const interval = setInterval(() => {
      if (!isDragging && !isFullscreen) {
        handleNextImage();
      }
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [caseStudy.gallery, isDragging, isFullscreen, handleNextImage]);

  // Smooth scroll to sections
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Print functionality
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Toast Notifications */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 right-4 z-[9999] px-6 py-3 rounded-lg shadow-xl ${
              toast.type === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon
                name={toast.type === "success" ? "CheckCircle" : "AlertCircle"}
                size={20}
              />
              <span className="font-medium">{toast.message}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
        className={`fixed inset-0 z-[100] bg-white ${className}`}
      >
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-20" />

        {/* Main viewer container */}
        <div
          ref={viewerRef}
          className="relative h-full w-full overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-transparent scrollbar-thumb-red-600/30"
        >
          {/* Dynamic Header */}
          <motion.header
            animate={{
              backdropFilter: isScrolled ? "blur(20px)" : "blur(0px)",
              backgroundColor: isScrolled
                ? "rgba(255,255,255,0.95)"
                : "rgba(255,255,255,0.85)",
              boxShadow: isScrolled ? "0 4px 20px rgba(0,0,0,0.08)" : "none",
            }}
            className="sticky top-0 z-50 border-b border-gray-100 transition-all duration-300"
          >
            <div className="container mx-auto px-2 py-2">
              <div className="flex items-center justify-between">
                {/* Left controls */}
                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05, x: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onClose}
                    className="group relative flex items-center gap-3 rounded-2xl bg-white px-2 sm:px-5 py-2 text-sm sm:text-lg shadow-lg border border-gray-200 hover:border-red-600/30 transition-all"
                    aria-label="Back to portfolio"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <Icon
                      name="ArrowLeft"
                      size={20}
                      className="text-gray-800 group-hover:text-red-600 transition-colors"
                    />
                    <span className="font-medium text-gray-800 group-hover:text-red-600 transition-colors">
                      Back
                    </span>
                  </motion.button>

                  {/* Functional Download Button */}
                  <motion.button
                    whileHover={{ scale: 1.05, x: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDownloadPDF}
                    disabled={isDownloading || !caseStudy.id}
                    className={`group relative flex items-center gap-3 rounded-2xl bg-white px-2 sm:px-5 py-2 text-sm sm:text-lg shadow-lg border border-gray-200 hover:border-red-600/30 transition-all ${
                      isDownloading
                        ? "bg-gray-100 border-gray-200 cursor-wait"
                        : "bg-white border-gray-200 hover:border-red-600/30"
                    }`}
                    aria-label="Download PDF"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    {isDownloading ? (
                      <>
                        <div className="animate-spin">
                          <Icon
                            name="Loader"
                            size={20}
                            className="text-red-600"
                          />
                        </div>
                        <span className="font-medium text-gray-800">
                          Downloading...
                        </span>
                      </>
                    ) : (
                      <>
                        <Icon
                          name="Download"
                          size={20}
                          className="text-gray-800 group-hover:text-red-600 transition-colors"
                        />
                        <span className="font-medium text-gray-800 group-hover:text-red-600 transition-colors">
                          PDF
                        </span>
                      </>
                    )}
                  </motion.button>

                  {/* Print Button */}
                  <motion.button
                    whileHover={{ scale: 1.05, x: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePrint}
                    className="group relative flex items-center gap-3 rounded-2xl bg-white px-2 sm:px-5 py-2 text-sm sm:text-lg shadow-lg border border-gray-200 hover:border-red-600/30 transition-all"
                    aria-label="Print case study"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <Icon
                      name="Printer"
                      size={20}
                      className="text-gray-800 group-hover:text-red-600 transition-colors"
                    />
                    <span className="font-medium text-gray-800 group-hover:text-red-600 transition-colors">
                      Print
                    </span>
                  </motion.button>
                </div>

                {/* Case Study Title (centered) */}
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="hidden md:block"
                >
                  <h1 className="text-lg font-semibold text-gray-800 truncate max-w-lg">
                    {caseStudy.title}
                  </h1>
                </motion.div>

                {/* Right controls */}
                <div className="flex items-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05, rotate: 90 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onClose}
                    className="group relative rounded-full bg-white p-3 shadow-lg border border-gray-200 hover:border-red-600/30 transition-all"
                    aria-label="Close case study"
                  >
                    <div className="absolute inset-0 rounded-full bg-red-600/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <Icon
                      name="X"
                      size={24}
                      className="text-gray-800 group-hover:text-red-600 transition-colors"
                    />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.header>

          {/* Main Content */}
          <div className="container mx-auto px-4 py-4 md:px-8 lg:px-12">
            {/* Hero Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="relative mb-4 sm:mb-6 overflow-hidden rounded-3xl shadow-2xl"
              id="hero"
            >
              <div className="relative w-full h-[30vh] min-h-[180px] sm:h-[50vh] sm:min-h-[400px] overflow-hidden rounded-3xl">
                {caseStudy.image ? (
                  <Image
                    src={caseStudy.image}
                    alt={caseStudy.alt || ""}
                    fill
                    priority
                    sizes="100vw"
                    className="!absolute inset-0 w-full h-full object-cover"
                  />
                ) : caseStudy.website ? (
                  <iframe
                    src={caseStudy.website}
                    className="absolute inset-0 w-full h-full"
                    loading="eager"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No content available
                  </div>
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
                  <div className="flex flex-col gap-6">
                    <div className="flex flex-wrap gap-3">
                      {caseStudy.services?.map((service, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.1 }}
                          className="rounded-full bg-red-600/70  px-4 py-1 text-xs sm:text-sm font-semibold text-white shadow-lg"
                        >
                          {service}
                        </motion.span>
                      ))}
                    </div>

                    <div className="space-y-1 sm:space-y-2">
                      <h1 className="text-2xl sm:text-3xl md:text-5xl  font-black text-white leading-tight">
                        {caseStudy.title}
                      </h1>

                      {(caseStudy.client || caseStudy.industry) && (
                        <p className="text-base sm:text-lg font-black text-white/90">
                          {caseStudy.client && (
                            <span className="text-red-500">
                              {caseStudy.client}
                            </span>
                          )}
                          {caseStudy.client && caseStudy.industry && " • "}
                          {caseStudy.industry}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Stats Overview */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-4 md:mb-8 "
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-4">
                {caseStudy.campaignDuration && (
                  <div className="group relative overflow-hidden rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/5 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative z-10">
                      <h3 className="text-sm sm:text-lg font-semibold text-red-600 mb-1">
                        Duration
                      </h3>
                      <p className="text-xl font-bold text-gray-900">
                        {new Date(
                          caseStudy.campaignDuration.start,
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}{" "}
                        –{" "}
                        {new Date(
                          caseStudy.campaignDuration.end,
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {caseStudy.budget && (
                  <div className="group relative overflow-hidden rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/5 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative z-10">
                      <h3 className="text-sm sm:text-lg font-semibold text-red-600 mb-1">
                        Budget
                      </h3>
                      <p className="text-xl font-bold text-gray-900">
                        {caseStudy.budget}
                      </p>
                    </div>
                  </div>
                )}

                {caseStudy.channelsUsed?.length > 0 && (
                  <div className="group relative overflow-hidden rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/5 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative z-10">
                      <h3 className="text-sm sm:text-lg font-semibold text-red-600 mb-1">
                        Channels
                      </h3>
                      <div className="flex flex-wrap gap-1">
                        {caseStudy.channelsUsed
                          .slice(0, 2)
                          .map((channel, i) => (
                            <span
                              key={i}
                              className="text-lg font-bold text-gray-600"
                            >
                              {channel}
                              {i < 1 ? ", " : ""}
                            </span>
                          ))}
                        {caseStudy.channelsUsed.length > 2 && (
                          <span className="text-base font-bold text-gray-400">
                            +{caseStudy.channelsUsed.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {caseStudy.toolsAndTechnology?.length > 0 && (
                  <div className="group relative overflow-hidden rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/5 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative z-10">
                      <h3 className="text-sm sm:text-lg font-semibold text-red-600 mb-1">
                        Technologies
                      </h3>
                      <p className="text-xl font-bold text-gray-900">
                        {caseStudy.toolsAndTechnology.length}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-8">
                {/* Overview */}
                {caseStudy.description && (
                  <motion.section
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="group"
                    id="overview"
                  >
                    <div className="flex items-center gap-2 mb-2 ">
                      <div className="h-10 w-1 bg-gradient-to-b from-red-600 to-red-400 rounded-full" />
                      <h2 className="text-2xl font-bold text-gray-900">
                        Overview
                      </h2>
                    </div>
                    <div className="relative rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200">
                      <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                      <p className="text-base sm:text-lg leading-relaxed text-gray-700 relative z-10">
                        {caseStudy.description}
                      </p>
                    </div>
                  </motion.section>
                )}

                {/* Challenge / Solution / Results Cards */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="grid md:grid-cols-3 gap-2 sm:gap-6"
                >
                  {caseStudy.challenge && (
                    <div className="group relative overflow-hidden rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300">
                      <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="rounded-lg shadow-subtle p-2">
                            <Icon
                              name="CircleSlash"
                              size={24}
                              className="text-red-600"
                            />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">
                            Challenge
                          </h3>
                        </div>
                        <p className=" text-base sm:text-lg text-gray-700">
                          {caseStudy.challenge}
                        </p>
                      </div>
                    </div>
                  )}

                  {caseStudy.solution && (
                    <div className="group relative overflow-hidden rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300">
                      <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="rounded-lg shadow-subtle p-2">
                            <Icon
                              name="Lightbulb"
                              size={24}
                              className="text-red-600"
                            />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">
                            Solution
                          </h3>
                        </div>
                        <p className="text-base sm:text-lg text-gray-700">
                          {caseStudy.solution}
                        </p>
                      </div>
                    </div>
                  )}

                  {caseStudy.results && (
                    <div className="group relative overflow-hidden rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300">
                      <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="rounded-lg shadow-subtle p-2">
                            <Icon
                              name="TrendingUp"
                              size={24}
                              className="text-red-600"
                            />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">
                            Results
                          </h3>
                        </div>
                        <p className="text-base sm:text-lg text-gray-700">
                          {caseStudy.results}
                        </p>
                      </div>
                    </div>
                  )}
                </motion.div>

                {/* Gallery Section */}
                <motion.section
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-2 sm:space-y-4 md:space-y-6"
                  id={caseStudy.gallery?.length > 0 ? "gallery" : "website"}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-4">
                      <div className="h-10 w-1 bg-gradient-to-b from-red-600 to-red-400 rounded-full" />

                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {caseStudy.gallery?.length > 0
                          ? "Gallery"
                          : "Website Display"}
                      </h2>

                      {caseStudy.gallery?.length > 0 && (
                        <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {currentGalleryIndex + 1} / {caseStudy.gallery.length}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ===== IF GALLERY EXISTS ===== */}
                  {caseStudy.gallery?.length > 0 ? (
                    <>
                      <div>
                        <div
                          ref={galleryRef}
                          className={`relative rounded-2xl overflow-hidden shadow-2xl bg-black cursor-grab active:cursor-grabbing ${isFullscreen ? "fixed inset-0 z-[9999] flex items-center justify-center" : ""}`}
                          onMouseDown={handleDragStart}
                          onMouseMove={handleDragMove}
                          onMouseUp={handleDragEnd}
                          onMouseLeave={handleDragEnd}
                        >
                          {/* Main Media */}
                          <div
                            className={`relative bg-black w-full aspect-video  ${isFullscreen ? "w-auto h-auto max-w-[95vw] max-h-95vh flex items-center justify-center" : ""}`}
                          >
                            {caseStudy.gallery[currentGalleryIndex].type ===
                            "video" ? (
                              <video
                                src={caseStudy.gallery[currentGalleryIndex].src}
                                crossOrigin="anonymous"
                                controls
                                className="w-full h-full object-contain"
                                autoPlay
                                loop
                                playsInline
                              />
                            ) : (
                              <Image
                                src={caseStudy.gallery[currentGalleryIndex].src}
                                alt={
                                  caseStudy.gallery[currentGalleryIndex].alt ||
                                  ""
                                }
                                crossOrigin="anonymous"
                                className="w-full h-full object-contain"
                                fill
                                sizes="100vw"
                              />
                            )}
                          </div>

                          {/* Controls */}
                          {caseStudy.gallery.length > 1 && (
                            <>
                              <button
                                onClick={toggleFullscreen}
                                className="absolute top-4 right-4 z-50 rounded-full bg-white p-3 shadow-lg border border-gray-200"
                              >
                                <Icon
                                  name={
                                    isFullscreen ? "Minimize2" : "Maximize2"
                                  }
                                  size={20}
                                />
                              </button>

                              <div className="absolute inset-0 flex items-center justify-between p-2">
                                <motion.button onClick={handlePrevImage}>
                                  <Icon name="ChevronLeft" size={24} />
                                </motion.button>

                                <motion.button onClick={handleNextImage}>
                                  <Icon name="ChevronRight" size={24} />
                                </motion.button>
                              </div>

                              {/* Thumbnails */}
                              <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                                <div className="flex gap-2 p-2 rounded-xl bg-white/20 backdrop-blur-sm border">
                                  {caseStudy.gallery.map((item, index) => (
                                    <button
                                      key={index}
                                      onClick={() =>
                                        setCurrentGalleryIndex(index)
                                      }
                                      className={`relative w-16 h-8 rounded-lg overflow-hidden ${
                                        index === currentGalleryIndex
                                          ? "ring-2 ring-red-600 scale-105"
                                          : "opacity-30 hover:opacity-80"
                                      }`}
                                    >
                                      <Image
                                        src={item.src}
                                        alt=""
                                        fill
                                        className="object-cover"
                                      />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Bottom Controls */}
                      <div className="flex justify-center gap-4">
                        <button
                          onClick={handlePrevImage}
                          className="px-5 py-2.5 rounded-xl bg-gray-900 text-white font-medium shadow-lg shadow-gray-900/20 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 active:shadow-md transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          ← Previous
                        </button>

                        <button
                          onClick={handleNextImage}
                          className="px-5 py-2.5 rounded-xl bg-red-600 text-white font-medium shadow-lg shadow-red-600/30 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 active:shadow-md transition-all duration-200"
                        >
                          Next →
                        </button>
                      </div>
                    </>
                  ) : (
                    /* ===== NO GALLERY → SHOW WEBSITE ===== */
                    <div className="relative h-[80vh] sm:h-[70vh] lg:h-[825px] rounded-2xl overflow-hidden shadow-2xl border">
                      {caseStudy.website ? (
                        <iframe
                          src={caseStudy.website}
                          className="w-full h-full"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          No website available
                        </div>
                      )}
                    </div>
                  )}
                </motion.section>
              </div>

              {/* Right Column - Sidebar */}
              <div className="space-y-4 sm:space-y-6">
                {/* Client Goals */}
                {caseStudy.clientGoals?.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="group relative rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300"
                  >
                    <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                    <div className="relative z-10">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 sm:mb-4 flex items-center gap-2">
                        <Icon
                          name="Target"
                          size={20}
                          className="text-red-600"
                        />
                        Client Goals
                      </h3>
                      <ul className="space-y-2 sm:space-y-3">
                        {caseStudy.clientGoals.map((goal, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 sm:gap-3"
                          >
                            <div className="mt-1 h-2 w-2 rounded-full bg-red-600" />
                            <span className="text-gray-700">{goal}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}

                {/* Team & Tools */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-4 sm:space-y-6"
                >
                  {caseStudy.teamRoles && (
                    <div className="group relative rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300">
                      <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                      <div className="relative z-10">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 sm:mb-4 flex items-center gap-2">
                          <Icon
                            name="Users"
                            size={20}
                            className="text-red-600"
                          />
                          Team
                        </h3>
                        <div className="space-y-2 sm:space-y-3">
                          {Object.entries(caseStudy.teamRoles).map(
                            ([role, name], i) => (
                              <div
                                key={i}
                                className="flex justify-between items-center"
                              >
                                <span className="text-gray-600">
                                  {formatRole(role)}
                                </span>

                                <span className="text-gray-900 font-medium">
                                  {name}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {caseStudy.toolsAndTechnology?.length > 0 && (
                    <div className="group relative rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300">
                      <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                      <div className="relative z-10">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 sm:mb-4 flex items-center gap-2">
                          <Icon name="Cpu" size={20} className="text-red-600" />
                          Technology Stack
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {caseStudy.toolsAndTechnology.map((tool, i) => (
                            <span
                              key={i}
                              className="rounded-full bg-red-600/10 px-3 py-1 text-xs sm:text-sm text-red-700 border border-red-600/20"
                            >
                              {tool}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>

                {/* Target Audience */}
                {caseStudy.targetAudience && (
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="group relative rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300"
                  >
                    <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                    <div className="relative z-10">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 sm:mb-4 flex items-center gap-2">
                        <Icon name="Users" size={20} className="text-red-600" />
                        Target Audience
                      </h3>
                      <div className="space-y-2 sm:space-y-3">
                        <div>
                          <span className="text-sm font-medium text-red-600">
                            Age Range:
                          </span>
                          <p className="text-gray-700">
                            {capitalizeWords(caseStudy.targetAudience.ageRange)}
                          </p>
                        </div>

                        <div>
                          <span className="text-sm font-medium text-red-600">
                            Demographics:
                          </span>
                          <p className="text-gray-700">
                            {caseStudy.targetAudience.demographics
                              .map(capitalizeWords)
                              .join(", ")}
                          </p>
                        </div>

                        <div>
                          <span className="text-sm font-medium text-red-600">
                            Psychographics:
                          </span>
                          <p className="text-gray-700">
                            {caseStudy.targetAudience.psychographics
                              .map(capitalizeWords)
                              .join(", ")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Awards & Recognition */}
                {caseStudy.awards?.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="group relative rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300"
                  >
                    <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                    <div className="relative z-10">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 sm:mb-4 flex items-center gap-2">
                        <Icon name="Award" size={20} className="text-red-600" />
                        Awards & Recognition
                      </h3>
                      <ul className="space-y-2 sm:space-y-3">
                        {caseStudy.awards.map((award, i) => (
                          <li key={i} className="flex items-start gap-2 sm:gap-3">
                            <div className="mt-1 h-2 w-2 rounded-full bg-yellow-500" />
                            <span className="text-gray-700">{award}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
{caseStudy.quote && (
  <motion.div
    initial={{ opacity: 0, x: 30 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.5 }}
    className="group relative rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300"
  >
    <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />

    <div className="relative z-10">
      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Icon name="Quote" size={20} className="text-red-600" />
        Client Testimonial
      </h3>

      <blockquote className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6">
        "{caseStudy.quote}"
      </blockquote>

      <div className="flex items-center gap-3">
        {caseStudy.testimonialImage && (
          <Image
            src={caseStudy.testimonialImage}
            alt={caseStudy.author}
            className="w-12 h-12 rounded-full object-cover"
          />
        )}

        <div>
          <div className="font-semibold text-gray-900">
            {caseStudy.author}
          </div>

          <div className="text-sm font-medium text-red-600">
            {caseStudy.position}
          </div>

          <div className="text-sm text-gray-500">
            {caseStudy.client}
          </div>
        </div>
      </div>
    </div>
  </motion.div>
)}

                {/* Share Section */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="group relative rounded-2xl bg-white p-4 sm:p-6 shadow-xl border border-gray-200 hover:border-red-600/30 transition-all duration-300"
                >
                  <div className="absolute -inset-1 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 sm:mb-4 ">
                      Share This Case Study
                    </h3>

                    {/* Improved Share Buttons Grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-3">
                      {[
                        {
                          type: "linkedin",
                          label: "LinkedIn",
                          color: "#0077B5",
                          icon: "Linkedin",
                          description: "Share with professionals",
                        },
                        {
                          type: "twitter",
                          label: "Twitter",
                          color: "#1DA1F2",
                          icon: "Twitter",
                          description: "Tweet this case study",
                        },
                        {
                          type: "facebook",
                          label: "Facebook",
                          color: "#1877F2",
                          icon: "Facebook",
                          description: "Share on Facebook",
                        },
                        {
                          type: "whatsapp",
                          label: "WhatsApp",
                          color: "#25D366",
                          icon: "MessageCircle",
                          description: "Share via WhatsApp",
                        },
                        {
                          type: "email",
                          label: "Email",
                          color: "#EA4335",
                          icon: "Mail",
                          description: "Share via email",
                        },
                        {
                          type: "link",
                          label: copiedLink ? "Copied!" : "Copy Link",
                          color: copiedLink ? "#10B981" : "#6B7280",
                          icon: copiedLink ? "Check" : "Link",
                          description: "Copy link to clipboard",
                        },
                      ].map((platform) => (
                        <motion.button
                          key={platform.type}
                          whileHover={{
                            scale:
                              selectedShareType === platform.type ? 1 : 1.05,
                            y: -2,
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleShare(platform.type)}
                          disabled={
                            isSharing && selectedShareType === platform.type
                          }
                          className="relative flex flex-col items-center justify-center gap-2 rounded-xl px-1 sm:px-2 py-2 text-white transition-all hover:shadow-lg disabled:cursor-wait"
                          style={{ backgroundColor: platform.color }}
                          aria-label={`Share on ${platform.label}`}
                        >
                          {/* Loading Spinner */}
                          {isSharing && selectedShareType === platform.type ? (
                            <div className="animate-spin">
                              <Icon name="Loader" size={20} />
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Icon name={platform.icon} size={20} />
                              <span className="text-xs font-medium">
                                {platform.label}
                              </span>
                            </div>
                          )}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>

                {/* Website Button */}
                {caseStudy.website && (
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className="group"
                  >
                    <a
                      href={caseStudy.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-3 w-full rounded-2xl bg-gradient-to-r from-red-600 to-red-500 px-6 py-4 text-white font-bold shadow-lg hover:shadow-xl hover:shadow-red-600/30 transition-all duration-300 hover:scale-[1.02]"
                    >
                      <Icon name="ExternalLink" size={20} />
                      Visit Project Website
                    </a>
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {/* Floating Navigation Dots */}
          <div className="fixed right-6 top-1/2 transform -translate-y-1/2 hidden lg:flex flex-col gap-4 z-50">
            {[
              { id: "hero", label: "Hero" },
              { id: "overview", label: "Overview" },
              { id: "gallery", label: "Gallery" },
            ].map((section, i) => (
              <button
                key={i}
                onClick={() => scrollToSection(section.id)}
                className="group relative flex items-center justify-center w-4 h-4 rounded-full bg-gray-700/40 hover:bg-red-600/80 shadow-md shadow-red-600/40 hover:shadow-lg hover:shadow-red-600/70 transition-all duration-300 before:absolute before:-right-10 before:px-3 before:py-1 before:rounded-md before:bg-gray-900/80 before:text-xs before:text-white before:opacity-0 before:scale-90 before:transition-all before:duration-300 group-hover:before:opacity-100 group-hover:before:scale-100"
                aria-label={`Jump to ${section.label}`}
                style={{ "--tw-content": `"${section.label}"` }}
              >
                {/* Glowing Dot */}
                <div className="w-full h-full rounded-full bg-red-600 animate-pulse" />
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default CaseStudyViewer;
